import {
  ApartmentOutlined,
  AuditOutlined,
  BankOutlined,
  CheckOutlined,
  CloudServerOutlined,
  DashboardOutlined,
  DesktopOutlined,
  DollarOutlined,
  DownOutlined,
  FileTextOutlined,
  FlagOutlined,
  GiftOutlined,
  KeyOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MobileOutlined,
  MoonOutlined,
  NotificationOutlined,
  PhoneOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  RiseOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  SoundOutlined,
  StopOutlined,
  SunOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Drawer, Dropdown, Layout, Menu, Spin, theme, Typography, type MenuProps } from 'antd';
import { Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { prefs, SIDER_KEY } from '../theme/storage';
import { useThemeMode, type ThemeMode } from '../theme/ThemeProvider';
import { brand } from '../theme/tokens';
import { BrandMark } from './BrandMark';
import { ChangePasswordModal } from './ChangePasswordModal';
import { ErrorBoundary } from './ErrorBoundary';
import { useResponsive } from './useResponsive';

interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  perm: string;
}

/** Menu entries are filtered by permission; the API enforces them regardless. */
const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Overview',
    items: [
      { key: '/', label: 'Dashboard', icon: <DashboardOutlined />, perm: 'analytics.view' },
      { key: '/finance', label: 'Finance', icon: <LineChartOutlined />, perm: 'wallet.view' },
    ],
  },
  {
    group: 'Live',
    items: [
      { key: '/live', label: 'Live Rooms', icon: <PlayCircleOutlined />, perm: 'live.view' },
      { key: '/pk', label: 'PK Battles', icon: <ThunderboltOutlined />, perm: 'live.view' },
      { key: '/calls', label: 'Calls', icon: <PhoneOutlined />, perm: 'live.view' },
      { key: '/games', label: 'Games', icon: <RocketOutlined />, perm: 'game.manage' },
    ],
  },
  {
    group: 'Users & Business',
    items: [
      { key: '/users', label: 'Users', icon: <TeamOutlined />, perm: 'user.view' },
      { key: '/host-requests', label: 'Host Requests', icon: <UserAddOutlined />, perm: 'host_request.review' },
      { key: '/agencies', label: 'Agencies', icon: <ApartmentOutlined />, perm: 'agency.manage' },
      { key: '/resellers', label: 'Resellers', icon: <BankOutlined />, perm: 'reseller.manage' },
    ],
  },
  {
    group: 'Content',
    items: [
      { key: '/posts', label: 'Posts', icon: <FileTextOutlined />, perm: 'content.moderate' },
      { key: '/announcements', label: 'Announcements', icon: <NotificationOutlined />, perm: 'settings.manage' },
      { key: '/banners', label: 'Home Banners', icon: <PictureOutlined />, perm: 'banner.manage' },
      { key: '/news-ticker', label: 'News Ticker', icon: <SoundOutlined />, perm: 'ticker.manage' },
    ],
  },
  {
    group: 'Economy',
    items: [
      { key: '/gifts', label: 'Gifts', icon: <GiftOutlined />, perm: 'gift.manage' },
      { key: '/store', label: 'Store', icon: <ShopOutlined />, perm: 'store.manage' },
      { key: '/levels', label: 'User Levels', icon: <RiseOutlined />, perm: 'settings.manage' },
      { key: '/recharge', label: 'Recharge', icon: <WalletOutlined />, perm: 'recharge.manage' },
      { key: '/withdrawals', label: 'Withdrawals', icon: <DollarOutlined />, perm: 'withdraw.approve' },
    ],
  },
  {
    group: 'Moderation',
    items: [
      { key: '/reports', label: 'Reports', icon: <FlagOutlined />, perm: 'report.view' },
      { key: '/moderation/banned', label: 'Banned Users', icon: <StopOutlined />, perm: 'user.view' },
      { key: '/moderation/devices', label: 'Banned Devices', icon: <MobileOutlined />, perm: 'user.view' },
      { key: '/audit', label: 'Audit Logs', icon: <AuditOutlined />, perm: 'audit.view' },
    ],
  },
  {
    group: 'System',
    items: [
      { key: '/roles', label: 'Roles & Permissions', icon: <SafetyCertificateOutlined />, perm: 'role.manage' },
      { key: '/settings', label: 'Settings', icon: <SettingOutlined />, perm: 'settings.manage' },
      { key: '/storage', label: 'Media Storage', icon: <CloudServerOutlined />, perm: 'settings.manage' },
    ],
  },
];

const ALL_ITEMS = NAV.flatMap((g) => g.items);

/** The nav entry that owns [pathname]: exact match for "/", otherwise the longest matching path prefix. */
function activeItem(pathname: string): NavItem | undefined {
  if (pathname === '/') return ALL_ITEMS[0];
  return ALL_ITEMS.filter((n) => n.key !== '/' && (pathname === n.key || pathname.startsWith(`${n.key}/`))).sort((a, b) => b.key.length - a.key.length)[0];
}

const THEME_OPTIONS: { key: ThemeMode; label: string; icon: ReactNode }[] = [
  { key: 'system', label: 'System', icon: <DesktopOutlined /> },
  { key: 'light', label: 'Light', icon: <SunOutlined /> },
  { key: 'dark', label: 'Dark', icon: <MoonOutlined /> },
];

const SIDER_WIDTH = 240;
const SIDER_COLLAPSED = 72;

export function AppLayout() {
  const { user, can, logout } = useAuth();
  const { pathname } = useLocation();
  const { token } = theme.useToken();
  const { mode, resolved, setMode } = useThemeMode();
  const { isCompact, isMobile } = useResponsive();
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => prefs.get(SIDER_KEY) === '1');
  const [pwOpen, setPwOpen] = useState(false);

  // Route change or growing to desktop closes the mobile nav.
  useEffect(() => setNavOpen(false), [pathname, isCompact]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      prefs.set(SIDER_KEY, c ? '0' : '1');
      return !c;
    });
  };

  const active = activeItem(pathname);
  const iconsOnly = !isCompact && collapsed;

  const menuItems = useMemo<MenuProps['items']>(() => {
    const groups = NAV.map((g) => ({ ...g, items: g.items.filter((n) => can(n.perm)) })).filter((g) => g.items.length > 0);
    const toItem = (n: NavItem) => ({ key: n.key, icon: n.icon, label: <Link to={n.key}>{n.label}</Link> });
    // Group headings don't fit an icon rail; separate the sections with dividers instead.
    if (iconsOnly) return groups.flatMap((g, i) => [...(i ? [{ type: 'divider' as const, key: `d-${g.group}` }] : []), ...g.items.map(toItem)]);
    return groups.map((g) => ({ type: 'group' as const, key: g.group, label: g.group, children: g.items.map(toItem) }));
  }, [can, iconsOnly]);

  const nav = (
    <>
      <Link to="/" className="cl-brand" aria-label="Cute Live Admin home" style={iconsOnly ? { justifyContent: 'center', paddingInline: 0 } : undefined}>
        <BrandMark className="cl-brand-mark" />
        {!iconsOnly && <span>Cute Live Admin</span>}
      </Link>
      <nav className="cl-nav-scroll" aria-label="Main">
        <Menu theme="dark" mode="inline" inlineCollapsed={iconsOnly} selectedKeys={active ? [active.key] : []} items={menuItems} style={{ borderInlineEnd: 0 }} />
      </nav>
    </>
  );

  const themeMenu: MenuProps = {
    selectable: true,
    selectedKeys: [mode],
    items: THEME_OPTIONS.map((o) => ({
      key: o.key,
      icon: o.icon,
      label: (
        <span style={{ display: 'inline-flex', gap: 24, justifyContent: 'space-between', width: '100%' }}>
          {o.label}
          {mode === o.key && <CheckOutlined />}
        </span>
      ),
    })),
    onClick: ({ key }) => setMode(key as ThemeMode),
  };

  const userMenu: MenuProps = {
    items: [
      {
        key: 'who',
        disabled: true,
        label: (
          <div style={{ lineHeight: 1.4, cursor: 'default' }}>
            <Typography.Text strong>{user?.displayName}</Typography.Text>
            {user?.email && (
              <>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {user.email}
                </Typography.Text>
              </>
            )}
          </div>
        ),
      },
      { type: 'divider' },
      { key: 'password', icon: <KeyOutlined />, label: 'Change password', onClick: () => setPwOpen(true) },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', danger: true, onClick: () => void logout() },
    ],
  };

  return (
    <Layout style={{ minHeight: '100dvh' }}>
      {!isCompact && (
        <Layout.Sider
          className="cl-sider"
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED}
          collapsed={collapsed}
          trigger={null}
          theme="dark"
          style={{ background: brand.background, borderInlineEnd: resolved === 'dark' ? `1px solid ${brand.outlineVariant}` : undefined }}
        >
          {nav}
        </Layout.Sider>
      )}

      {isCompact && (
        <Drawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          placement="left"
          width={Math.min(SIDER_WIDTH + 40, window.innerWidth - 48)}
          closable={false}
          title={null}
          styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', background: brand.background }, wrapper: { boxShadow: token.boxShadowSecondary } }}
          rootClassName="cl-nav-drawer"
          aria-label="Main navigation"
        >
          {nav}
        </Drawer>
      )}

      <Layout style={{ minWidth: 0 }}>
        <header
          className="cl-header"
          style={{
            background: resolved === 'dark' ? token.colorBgLayout : token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          {isCompact ? (
            <Button type="text" icon={<MenuOutlined />} aria-label="Open navigation" aria-expanded={navOpen} onClick={() => setNavOpen(true)} />
          ) : (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={toggleCollapsed}
            />
          )}
          {isCompact && (
            <Link to="/" aria-label="Cute Live Admin home" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, color: token.colorText }}>
              <BrandMark size={28} />
              <Typography.Text strong ellipsis style={{ fontSize: 16 }}>
                Cute Live Admin
              </Typography.Text>
            </Link>
          )}
          <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Dropdown menu={themeMenu} trigger={['click']} placement="bottomRight">
              <Button type="text" aria-label="Theme" icon={resolved === 'dark' ? <MoonOutlined /> : <SunOutlined />} />
            </Dropdown>
            <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
              <Button type="text" aria-label="Account menu" style={{ height: 40, paddingInline: isMobile ? 4 : 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar size={30} style={{ background: brand.primaryContainer, color: brand.onPrimaryContainer, flex: 'none' }}>
                  {user?.displayName?.[0]?.toUpperCase() ?? '?'}
                </Avatar>
                {!isMobile && (
                  <>
                    <Typography.Text ellipsis style={{ maxWidth: 160 }}>
                      {user?.displayName}
                    </Typography.Text>
                    <DownOutlined style={{ fontSize: 10, color: token.colorTextTertiary }} />
                  </>
                )}
              </Button>
            </Dropdown>
          </div>
        </header>
        <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
        <Layout.Content className="cl-content">
          <ErrorBoundary key={pathname}>
            <Suspense fallback={<PageSpinner />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export function PageSpinner() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
      <Spin size="large" />
    </div>
  );
}
